import { useState, useContext, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useTemplates = () => {
  const { baseUrl, basicAuth, userID } = useContext(GlobalContext);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const postRequest = useCallback(
    async (endpoint, body) => {
      if (!baseUrl || !basicAuth) return null;

      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      return res.json();
    },
    [baseUrl, basicAuth],
  );

  /* ===============================
     GET LANGUAGES
  =============================== */
  const getLanguages = useCallback(async () => {
    if (!userID) return null;

    try {
      setLoading(true);

      const data = await postRequest("/Whatsapp/v1.0/getLanguages", {
        userid: userID,
        secret: "N",
      });

      return data;
    } catch (err) {
      console.error("Get Languages Error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userID, postRequest]);

  /* ===============================
     CREATE TEXT TEMPLATE
  =============================== */
  const createTemplate = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest(
          "/Whatsapp/v1.0/createTemplate",
          payload,
        );

        return data;
      } catch (err) {
        console.error("Create Template Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

  /* ===============================
     CREATE IMAGE TEMPLATE
  =============================== */
  const createImageTemplate = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest(
          "/Whatsapp/v1.0/createImageTemplate",
          payload,
        );

        return data;
      } catch (err) {
        console.error("Create Image Template Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

  /* ===============================
     CREATE VIDEO TEMPLATE
  =============================== */
  const createVideoTemplate = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest(
          "/Whatsapp/v1.0/createVideoTemplate",
          payload,
        );

        return data;
      } catch (err) {
        console.error("Create Video Template Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

  /* ===============================
     CREATE DOCUMENT TEMPLATE
  =============================== */
  const createDocumentTemplate = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest(
          "/Whatsapp/v1.0/createDocumentTemplate",
          payload,
        );

        return data;
      } catch (err) {
        console.error("Create Document Template Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

const fetchVendorTemplates = useCallback(async (phoneNumberId) => {
  if (!userID) return;

  const payload = {
    userid: userID,
    phoneNumberId: phoneNumberId,
  };

  try {
    setLoading(true);

    const data = await postRequest("/Whatsapp/v1.0/getTemplates", payload);
    if (data?.responseCode === "00") {

      const templateData = data?.response?.data;

      if (Array.isArray(templateData)) {
        setTemplates(templateData);
      } else if (templateData) {
        setTemplates([templateData]);
      } else {
        setTemplates([]);
      }

    } else {
      setTemplates([]);
    }

  } catch (err) {
    console.error("Vendor Templates API Error:", err);
    setTemplates([]);
  } finally {
    setLoading(false);
  }

}, [userID, postRequest]);

  const deleteVendorTemplate = useCallback(
    async (templateId, phoneNumberId = "") => {
      if (!userID) return null;

      try {
        setLoading(true);

        const data = await postRequest("/Whatsapp/deleteTemplate", {
          userid: userID,
          templateId,
        });

        if (data?.responseCode === "00") {
          await fetchVendorTemplates(phoneNumberId);
        }

        return data;
      } catch (err) {
        console.error("Delete Vendor Template Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, postRequest, fetchVendorTemplates],
  );

  const getTemplateDetails = useCallback(
    async (templateId) => {
      if (!userID || !templateId) return null;

      try {
        setLoading(true);

        const data = await postRequest("/Whatsapp/getTemplateDetails", {
          userid: userID,
          secret: "string",
          templateId,
        });

        if (data?.responseCode === "00") {
          return data.response;
        }

        return null;
      } catch (err) {
        console.error("Template Details Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, postRequest],
  );
  
/*  EXECUTE TEMPLATE */

const executeTemplate = useCallback(
  async (payload) => {
    if (!userID) return null;

    try {
      setLoading(true);

      const finalPayload = {
        ...payload,
        userid: userID,
        messaging_product: "whatsapp",
        execute_type: "N",
        secret: "N",
      };

      const data = await postRequest(
        "/Whatsapp/executeTemplate",
        finalPayload
      );

      return data;
    } catch (err) {
      console.error("Execute Template Error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  },
  [userID, postRequest]
);

        /* GET LIBRARY TEMPLATES */

  const fetchLibraryTemplates = useCallback(async () => {
    if (!userID) return [];

    try {
      setLoading(true);

      const data = await postRequest("/Whatsapp/getLibraryTemplates", {
        userid: userID,
      });

      if (data?.responseCode === "00") {
        return data?.response || [];
      }

      return [];
    } catch (err) {
      console.error("Library Templates Error:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userID, postRequest]);

  const fetchTemplatesByUser = useCallback(async () => {
  if (!userID) return [];

  try {
    setLoading(true);

    const data = await postRequest("/Whatsapp/getTemplates", {
      userid: userID,
    });

    if (data?.responseCode === "00") {
      const templateData = data?.response?.data; 

      if (Array.isArray(templateData)) {
        setTemplates(templateData);  
      } else {
        setTemplates([]);
      }

      return templateData;
    }

    setTemplates([]);
    return [];

  } catch (err) {
    console.error("Get Templates API Error:", err);
    setTemplates([]);
    return [];
  } finally {
    setLoading(false);
  }
}, [userID, postRequest]);

  return {
    templates,
    loading,
    getLanguages,
    createTemplate,
    createImageTemplate,
    createVideoTemplate,
    createDocumentTemplate,
      fetchTemplatesByUser, 
    fetchVendorTemplates,
    deleteVendorTemplate,
    getTemplateDetails,
    executeTemplate,
    fetchLibraryTemplates,
  };
};

export default useTemplates;
